import type { Point } from '../types';
export interface ViewportState {
  panX: number;
  panY: number;
  scale: number;
}

export interface SerializableViewportState extends ViewportState {
  gridEnabled: boolean;
  snapEnabled: boolean;
  inverted: boolean;
}

export interface GridLineRange {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const getCanvasScreenPoint = (
  canvas: HTMLCanvasElement,
  event: MouseEvent | PointerEvent
): Point => {
  const rect = canvas.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
};

export const getCanvasWorldPoint = (
  canvas: HTMLCanvasElement,
  event: MouseEvent | PointerEvent,
  viewport: ViewportState
): Point => screenToWorld(getCanvasScreenPoint(canvas, event), viewport);

export const getWheelZoomFactor = (event: WheelEvent): number | null => {
  // Detect if this is a trackpad or mouse wheel:
  // - deltaMode 1 = line mode (mouse wheel)
  // - deltaMode 0 = pixel mode (trackpad)
  // - ctrlKey = pinch-to-zoom gesture on trackpad
  const isMouseWheel = event.deltaMode === 1 || Math.abs(event.deltaY) >= 40;
  const isPinchZoom = event.ctrlKey;
  const shouldZoom = isMouseWheel || isPinchZoom || event.shiftKey;

  if (!shouldZoom) return null;
  return event.deltaY > 0 ? 0.95 : 1.05;
};

export const screenToWorld = (screenPoint: Point, viewport: ViewportState): Point => ({
  x: (screenPoint.x - viewport.panX) / viewport.scale,
  y: (screenPoint.y - viewport.panY) / viewport.scale,
});

export const worldToScreen = (worldPoint: Point, viewport: ViewportState): Point => ({
  x: worldPoint.x * viewport.scale + viewport.panX,
  y: worldPoint.y * viewport.scale + viewport.panY,
});

export const zoomAtPoint = (
  viewport: ViewportState,
  screenPoint: Point,
  factor: number,
  minScale: number,
  maxScale: number
): ViewportState => {
  const nextScale = clamp(viewport.scale * factor, minScale, maxScale);
  const scaleChange = nextScale / viewport.scale;
  return {
    scale: nextScale,
    panX: screenPoint.x - (screenPoint.x - viewport.panX) * scaleChange,
    panY: screenPoint.y - (screenPoint.y - viewport.panY) * scaleChange,
  };
};

export const getGridLineRange = (
  width: number,
  height: number,
  viewport: ViewportState,
  gridSize: number,
  paddingCells = 0
): GridLineRange => {
  const minX = -viewport.panX / viewport.scale;
  const minY = -viewport.panY / viewport.scale;
  const maxX = (width - viewport.panX) / viewport.scale;
  const maxY = (height - viewport.panY) / viewport.scale;
  const padding = gridSize * paddingCells;

  return {
    minX,
    minY,
    maxX,
    maxY,
    startX: Math.floor(minX / gridSize) * gridSize - padding,
    startY: Math.floor(minY / gridSize) * gridSize - padding,
    endX: Math.ceil(maxX / gridSize) * gridSize + padding,
    endY: Math.ceil(maxY / gridSize) * gridSize + padding,
  };
};

export const snapToGrid = <T extends Point>(point: T, gridSize: number): T => ({
  ...point,
  x: Math.round(point.x / gridSize) * gridSize,
  y: Math.round(point.y / gridSize) * gridSize,
});
